<template>
  <div class="deviceSelectWrapper" :class="{ open: isOpen, disabled }" ref="wrapperRef">
    <div
      ref="triggerRef"
      class="deviceSelectTrigger"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-disabled="disabled"
      tabindex="0"
      @click="onTriggerClick"
      @focus="onTriggerFocus"
      @blur="onTriggerBlur"
      @keydown="onTriggerKeydown"
    >
      <span class="deviceSelectLabel" :class="{ placeholder: isPlaceholder }">
        {{ displayLabel }}
      </span>
      <FontAwesomeIcon :icon="faChevronDown" class="deviceSelectIcon" />
    </div>

    <transition name="deviceSelectFade">
      <ul
        v-if="isOpen"
        ref="listRef"
        class="deviceSelectOptions"
        role="listbox"
        :aria-activedescendant="activeOptionId"
      >
        <li
          v-for="(option, index) in normalizedOptions"
          :key="option.value || index"
          :id="optionId(index)"
          class="deviceSelectOption"
          :class="{
            selected: option.value === modelValue,
            highlighted: index === highlightedIndex,
            disabled: option.disabled
          }"
          role="option"
          :aria-selected="option.value === modelValue"
          tabindex="-1"
          @mousedown.prevent="onOptionMouseDown(option)"
          @mouseenter="highlightOption(index)"
        >
          {{ option.label }}
        </li>
        <li v-if="!normalizedOptions.length" class="deviceSelectOption empty" tabindex="-1">
          No audio devices found
        </li>
      </ul>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'

type BaseOption = {
  value: string
  label: string
  disabled?: boolean
}

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  options: {
    type: Array as () => BaseOption[],
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Please select a device'
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'focus'): void
  (e: 'blur'): void
  (e: 'change', value: string): void
}>()

const wrapperRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const highlightedIndex = ref(-1)
const hasEmittedFocus = ref(false)
const preventNextFocusEmit = ref(false)

const normalizedOptions = computed(() => props.options || [])

const selectedOption = computed(() =>
  normalizedOptions.value.find((option) => option.value === props.modelValue)
)

const displayLabel = computed(() => selectedOption.value?.label || props.placeholder)
const isPlaceholder = computed(() => !selectedOption.value)

const activeOptionId = computed(() =>
  highlightedIndex.value >= 0 ? optionId(highlightedIndex.value) : undefined
)

watch(
  () => props.modelValue,
  (newValue) => {
    if (!normalizedOptions.value.some((option) => option.value === newValue)) {
      highlightedIndex.value = -1
      return
    }

    highlightedIndex.value = normalizedOptions.value.findIndex(
      (option) => option.value === newValue && !option.disabled
    )
  },
  { immediate: true }
)

const addGlobalListeners = () => {
  document.addEventListener('pointerdown', onGlobalPointerDown, true)
}

const removeGlobalListeners = () => {
  document.removeEventListener('pointerdown', onGlobalPointerDown, true)
}

onMounted(() => {
  if (props.disabled) return
  highlightedIndex.value = normalizedOptions.value.findIndex(
    (option) => option.value === props.modelValue && !option.disabled
  )
})

onBeforeUnmount(() => {
  removeGlobalListeners()
})

function beginInteraction() {
  if (hasEmittedFocus.value) return
  hasEmittedFocus.value = true
  emit('focus')
}

function endInteraction() {
  if (!hasEmittedFocus.value) return
  hasEmittedFocus.value = false
  emit('blur')
}

function openDropdown(origin: 'mouse' | 'keyboard' = 'mouse') {
  if (props.disabled || isOpen.value) return
  isOpen.value = true
  beginInteraction()
  addGlobalListeners()

  if (highlightedIndex.value < 0) {
    highlightedIndex.value = normalizedOptions.value.findIndex((option) => !option.disabled)
  }

  nextTick(() => {
    if (origin === 'keyboard') {
      scrollHighlightedIntoView()
    }
  })
}

function closeDropdown(emitBlur: boolean = true) {
  if (!isOpen.value) return
  isOpen.value = false
  removeGlobalListeners()
  highlightedIndex.value = normalizedOptions.value.findIndex(
    (option) => option.value === props.modelValue && !option.disabled
  )
  if (emitBlur) {
    endInteraction()
  }
}

function toggleDropdown() {
  if (isOpen.value) {
    closeDropdown()
  } else {
    openDropdown()
  }
}

function onTriggerClick(event: MouseEvent) {
  event.preventDefault()
  if (props.disabled) return
  toggleDropdown()
}

function onTriggerFocus() {
  if (preventNextFocusEmit.value) {
    preventNextFocusEmit.value = false
    return
  }
  beginInteraction()
}

function onTriggerBlur(event: FocusEvent) {
  if (wrapperRef.value && wrapperRef.value.contains(event.relatedTarget as Node)) return
  if (isOpen.value) return
  endInteraction()
}

function onTriggerKeydown(event: KeyboardEvent) {
  if (props.disabled) return
  switch (event.key) {
    case 'ArrowDown':
    case 'Down':
      event.preventDefault()
      if (!isOpen.value) {
        openDropdown('keyboard')
      } else {
        moveHighlight(1)
      }
      break
    case 'ArrowUp':
    case 'Up':
      event.preventDefault()
      if (!isOpen.value) {
        openDropdown('keyboard')
      } else {
        moveHighlight(-1)
      }
      break
    case 'Enter':
    case ' ':
    case 'Spacebar':
      event.preventDefault()
      if (!isOpen.value) {
        openDropdown('keyboard')
      } else {
        selectHighlightedOption()
      }
      break
    case 'Escape':
    case 'Esc':
      if (isOpen.value) {
        event.preventDefault()
        closeDropdown()
        preventNextFocusEmit.value = true
        triggerRef.value?.blur()
        nextTick(() => triggerRef.value?.focus())
      }
      break
    case 'Tab':
      closeDropdown(false)
      endInteraction()
      break
    default:
      break
  }
}

function moveHighlight(direction: number) {
  if (!normalizedOptions.value.length) return
  let index = highlightedIndex.value
  const total = normalizedOptions.value.length

  for (let i = 0; i < total; i += 1) {
    index = (index + direction + total) % total
    if (!normalizedOptions.value[index]?.disabled) {
      highlightedIndex.value = index
      scrollHighlightedIntoView()
      return
    }
  }
}

function optionId(index: number) {
  return `device-select-option-${index}`
}

function selectHighlightedOption() {
  if (highlightedIndex.value < 0) return
  const option = normalizedOptions.value[highlightedIndex.value]
  if (!option || option.disabled) return
  commitSelection(option)
}

function onOptionMouseDown(option: BaseOption) {
  if (option.disabled) return
  commitSelection(option)
}

function commitSelection(option: BaseOption) {
  emit('update:modelValue', option.value)
  emit('change', option.value)
  closeDropdown()
  preventNextFocusEmit.value = true
  triggerRef.value?.blur()
  nextTick(() => triggerRef.value?.focus())
}

function highlightOption(index: number) {
  if (normalizedOptions.value[index]?.disabled) return
  highlightedIndex.value = index
}

function scrollHighlightedIntoView() {
  const list = listRef.value
  if (!list) return
  const item = list.children[highlightedIndex.value] as HTMLElement | undefined
  if (!item) return

  const itemTop = item.offsetTop
  const itemBottom = itemTop + item.offsetHeight
  if (itemTop < list.scrollTop) {
    list.scrollTop = itemTop
  } else if (itemBottom > list.scrollTop + list.clientHeight) {
    list.scrollTop = itemBottom - list.clientHeight
  }
}

function onGlobalPointerDown(event: PointerEvent) {
  if (!wrapperRef.value) return
  const target = event.target as Node | null
  if (wrapperRef.value.contains(target as Node)) return
  closeDropdown()
}

defineExpose({
  blur: () => {
    closeDropdown()
    triggerRef.value?.blur()
  },
  close: () => closeDropdown(),
  focus: () => triggerRef.value?.focus()
})
</script>

<style scoped lang="scss">
.deviceSelectWrapper {
  position: relative;
  width: 100%;

  &.disabled {
    opacity: 0.7;
    pointer-events: none;
  }
}

.deviceSelectTrigger {
  width: 100%;
  padding: 0.5rem 0.5rem 0.5rem 0.75rem;
  border: 1px solid $secondary;
  border-radius: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  cursor: pointer;
  background-color: $white;
  position: relative;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 2px rgba($primary, 0.2);
  }
}

.deviceSelectLabel.placeholder {
  color: $secondary;
  font-weight: 500;
}

.deviceSelectIcon {
  pointer-events: none;
  font-size: 0.9rem;
  color: $dark;
}

.deviceSelectOptions {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid $secondary;
  border-radius: 6px;
  background-color: $white;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
  z-index: 10;
  padding: 0.25rem 0;
  list-style: none;
}

.deviceSelectOption {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  font-size: 0.95rem;

  &.highlighted {
    background-color: lighten($primary, 40%);
    color: $dark;
  }

  &.selected {
    font-weight: 600;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.empty {
    cursor: default;
    text-align: center;
    color: $secondary;
  }
}

.deviceSelectFade-enter-active,
.deviceSelectFade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.deviceSelectFade-enter-from,
.deviceSelectFade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
